import { Typography } from "antd";
import { companyBlock, intro, lastUpdated, legalTitle, sectionBody, sectionHeading } from "../LegalPage.styles.ts";

const { Title, Paragraph, Text } = Typography;

export const PrivacyEn = () => (
  <article>
    <Title className={legalTitle} level={1}>
      Privacy Policy
    </Title>
    <Text className={lastUpdated}>Last updated: 6 May 2026</Text>

    <Paragraph className={intro}>
      This Privacy Policy explains how <strong>Фууд Сейфти Консултинг ЕООД</strong> (“Calculabel”, “we”, “us”) collects,
      uses, and protects personal data when you use the Calculabel service (the “Service”) at calculabel.com and related
      domains. We act as the data controller for the personal data described below within the meaning of Regulation (EU)
      2016/679 (GDPR) and the Bulgarian Personal Data Protection Act.
    </Paragraph>

    <Title className={sectionHeading} level={4}>
      1. About this policy
    </Title>
    <Paragraph className={sectionBody}>
      This policy applies to all visitors and registered users of the Service. By creating an account or otherwise using
      the Service, you acknowledge that you have read this policy. We may update it from time to time — see “Changes to
      this policy” below.
    </Paragraph>

    <Title className={sectionHeading} level={4}>
      2. What data we collect
    </Title>
    <div className={sectionBody}>
      <Paragraph>We collect and process the following categories of personal data:</Paragraph>
      <ul>
        <li>
          <strong>Account data</strong> — email address, full name, language preference, and the team you belong to.
        </li>
        <li>
          <strong>Authentication data</strong> — short-lived single-use tokens used by our passwordless email sign-in,
          and an encrypted session cookie kept while you are signed in.
        </li>
        <li>
          <strong>Content you create</strong> — recipes, ingredients, producers, files, labels, and other content you
          add to your team workspace.
        </li>
        <li>
          <strong>Usage data</strong> — server logs of requests made to the Service, error reports, and aggregated
          metrics needed to operate and secure the platform.
        </li>
        <li>
          <strong>Technical data</strong> — IP address, user-agent / browser type, device and OS, and approximate
          location derived from IP, used for security and abuse prevention.
        </li>
      </ul>
    </div>

    <Title className={sectionHeading} level={4}>
      3. Why we process it and on what legal basis
    </Title>
    <div className={sectionBody}>
      <ul>
        <li>
          <strong>Performance of a contract</strong> (Art. 6(1)(b) GDPR) — to create and maintain your account,
          authenticate you, and provide the features you request.
        </li>
        <li>
          <strong>Legitimate interests</strong> (Art. 6(1)(f) GDPR) — to keep the Service secure, prevent fraud and
          abuse, diagnose problems, and improve our product. You may object to processing based on legitimate interest
          at any time (see “Your rights”).
        </li>
        <li>
          <strong>Legal obligation</strong> (Art. 6(1)(c) GDPR) — to comply with Bulgarian and EU law (for example,
          accounting and tax obligations once paid plans are introduced).
        </li>
        <li>
          <strong>Consent</strong> (Art. 6(1)(a) GDPR) — only where specifically requested. You can withdraw consent at
          any time without affecting the lawfulness of processing carried out before withdrawal.
        </li>
      </ul>
    </div>

    <Title className={sectionHeading} level={4}>
      4. How long we keep your data
    </Title>
    <div className={sectionBody}>
      <ul>
        <li>
          <strong>Account and content data</strong> — for as long as your account is active. After you delete your
          account we keep residual data for up to 12 months to allow account recovery and to investigate abuse, after
          which it is deleted or anonymised.
        </li>
        <li>
          <strong>Backups</strong> — encrypted backups are retained for up to 90 days and then overwritten on a rolling
          schedule.
        </li>
        <li>
          <strong>Server logs</strong> — kept for up to 90 days for security and operational purposes.
        </li>
        <li>
          <strong>Accounting and invoicing records</strong> — retained for 10 years as required by Bulgarian accounting
          law (once paid plans are active).
        </li>
      </ul>
    </div>

    <Title className={sectionHeading} level={4}>
      5. Sharing with third parties (subprocessors)
    </Title>
    <div className={sectionBody}>
      <Paragraph>
        We do not sell your personal data. We share it only with carefully selected service providers that help us run
        Calculabel, each bound by appropriate data-processing terms:
      </Paragraph>
      <ul>
        <li>
          <strong>OVH</strong> (France, EU) — server infrastructure and hosting.
        </li>
        <li>
          <strong>DigitalOcean</strong> (EU region) — application hosting and virtual machines.
        </li>
        <li>
          <strong>Cloudflare</strong> — DNS, content delivery network, TLS termination, web application firewall, and
          DDoS protection.
        </li>
        <li>
          <strong>ZeptoMail</strong> (Zoho) — transactional email delivery (sign-in links, account notifications).
        </li>
        <li>
          <strong>Google LLC</strong> (Google Cloud, Gemini API) — AI-powered processing of uploaded product-label and
          recipe images for ingredient and nutrition data extraction. Per Google’s terms, customer data submitted to
          Gemini via the API is not used to train Google’s models.
        </li>
      </ul>
      <Paragraph>
        We do <strong>not</strong> use third-party analytics, advertising, marketing, or social-media tracking services.
      </Paragraph>
      <Paragraph>
        We may also disclose personal data if required by law, by a court order, or by a competent public authority.
      </Paragraph>
    </div>

    <Title className={sectionHeading} level={4}>
      6. International data transfers
    </Title>
    <Paragraph className={sectionBody}>
      Our primary infrastructure (OVH and DigitalOcean) is located within the European Union. Cloudflare, ZeptoMail, and
      Google (Cloud / Gemini) operate global networks that may route or process data outside the EU/EEA, including in
      the United States. Such transfers are protected by the European Commission’s Standard Contractual Clauses and the
      providers’ published data-protection terms.
    </Paragraph>

    <Title className={sectionHeading} level={4}>
      7. Your rights
    </Title>
    <div className={sectionBody}>
      <Paragraph>Subject to GDPR, you have the right to:</Paragraph>
      <ul>
        <li>access the personal data we hold about you and obtain a copy;</li>
        <li>have inaccurate or incomplete data corrected;</li>
        <li>request deletion (“right to be forgotten”) where applicable;</li>
        <li>request restriction of processing;</li>
        <li>object to processing based on legitimate interests;</li>
        <li>receive your data in a portable, machine-readable format;</li>
        <li>withdraw consent at any time, where processing is based on consent;</li>
        <li>
          lodge a complaint with the Bulgarian Commission for Personal Data Protection (КЗЛД), 2 Prof. Tsvetan Lazarov
          Blvd., 1592 Sofia, Bulgaria — <a href="https://www.cpdp.bg">www.cpdp.bg</a>.
        </li>
      </ul>
      <Paragraph>
        To exercise any of these rights, write to <a href="mailto:support@calculabel.com">support@calculabel.com</a>. We
        will respond within one month, as required by GDPR.
      </Paragraph>
    </div>

    <Title className={sectionHeading} level={4}>
      8. Cookies and similar technologies
    </Title>
    <Paragraph className={sectionBody}>
      Calculabel uses <strong>only strictly necessary cookies</strong>: an encrypted session cookie that keeps you
      signed in, and a small cookie that remembers your language preference. We do <strong>not</strong> use analytics,
      advertising, marketing, or third-party tracking cookies. For this reason no cookie consent banner is required
      under the ePrivacy Directive and GDPR.
    </Paragraph>

    <Title className={sectionHeading} level={4}>
      9. Security
    </Title>
    <Paragraph className={sectionBody}>
      We protect your data with HTTPS / HTTP/3 encryption in transit, encrypted session cookies, passwordless
      authentication via short-lived single-use email links, network firewalls and access controls, and regular backups.
      No system can be guaranteed 100% secure, but we work continuously to apply industry-standard safeguards.
    </Paragraph>

    <Title className={sectionHeading} level={4}>
      10. Children
    </Title>
    <Paragraph className={sectionBody}>
      Calculabel is not directed at children under 16, and we do not knowingly collect personal data from them. If you
      believe a child has provided us with personal data, please contact us and we will delete it.
    </Paragraph>

    <Title className={sectionHeading} level={4}>
      11. Changes to this policy
    </Title>
    <Paragraph className={sectionBody}>
      We may update this Privacy Policy from time to time. The “Last updated” date at the top will reflect the latest
      revision. For material changes we will provide additional notice — for example, an in-app banner or an email to
      registered users — before the changes take effect.
    </Paragraph>

    <Title className={sectionHeading} level={4}>
      12. Contact
    </Title>
    <Paragraph className={sectionBody}>
      Questions about this policy or about how we handle your data? Write to{" "}
      <a href="mailto:support@calculabel.com">support@calculabel.com</a>.
    </Paragraph>

    <Paragraph className={companyBlock}>
      Data controller: Фууд Сейфти Консултинг ЕООД
      <br />
      ЕИК / UIC: 204435984
      <br />
      VAT (ДДС №): BG204435984
      <br />
      Address: ул. Тракия № 34, 1527 София, България
      <br />
      Email: <a href="mailto:support@calculabel.com">support@calculabel.com</a>
    </Paragraph>
  </article>
);
